/**
 * Namespace `crm` — Mini-CRM Phase 1 (Kommunikations-Timeline in der
 * Patientenakte, freie Mail, Dashboard-Badge) + Phase 2a (Posteingang).
 * Siehe docs/CRM-PLAN.md.
 */
export const crm = {
  de: {
    crm: {
      // Inbox-Zustand eingehender Mails — geteilt zwischen Posteingang und Timeline.
      state: {
        new: 'Neu',
        read: 'Gelesen',
        answered: 'Beantwortet',
        unmatched: 'Unzugeordnet',
      },
      inbox: {
        eyebrow: 'Kommunikation',
        title: 'Posteingang',
        description: 'Eingehende Patienten-E-Mails lesen, zuordnen und beantworten.',
        filterNew: 'Neu',
        filterUnmatched: 'Unzugeordnet',
        filterAnswered: 'Beantwortet',
        filterAll: 'Alle',
        reload: 'Neu laden',
        loading: 'Lade…',
        errorLoad: 'Posteingang konnte nicht geladen werden',
        emptyTitle: 'Keine Nachrichten',
        emptyDescription: 'In diesem Filter liegen keine Nachrichten.',
        loadMore: 'Mehr laden',
        toPatient: 'Zur Akte',
        unknownSender: '(unbekannter Absender)',
        smartaiLabel: 'SmartAI-Anliegen (Website-Panel)',
        attachments: 'Anhänge',
        attachmentsSkipped_one: '{{count}} Anhang wurde beim Empfang übersprungen (Typ oder Größe nicht erlaubt).',
        attachmentsSkipped_other: '{{count}} Anhänge wurden beim Empfang übersprungen (Typ oder Größe nicht erlaubt).',
        downloadFailed: 'Anhang konnte nicht geladen werden',
        reply: 'Antworten',
        replyLoadFailed: 'Patientendaten konnten nicht geladen werden',
        assign: 'Patient zuordnen',
        assignTitle: 'Patient zuordnen',
        assignHint: 'Diese Mail konnte keiner Akte zugeordnet werden. Patienten suchen und zuordnen:',
        assignSaveAlternate: 'Adresse als Zweit-E-Mail speichern',
        assignConfirm: 'Zuordnen',
        assigning: 'Ordne zu…',
        assignOk: 'Patient zugeordnet',
        assignOkAlternate: 'Patient zugeordnet, Adresse als Zweit-E-Mail gespeichert',
        assignFailed: 'Zuordnung fehlgeschlagen',
        markReadFailed: 'Konnte nicht als gelesen markiert werden',
      },
      timeline: {
        title: 'Kommunikation',
        sendEmail: 'E-Mail senden',
        showInternal: 'interne anzeigen',
        hideInternal: 'interne ausblenden',
        loading: 'Lade…',
        errorLoad: 'Kommunikation konnte nicht geladen werden',
        empty: 'Noch keine Kommunikation.',
        emptyFiltered: 'Nur praxisinterne Einträge vorhanden.',
        showAll: 'Alle {{count}} Einträge anzeigen',
        noSubject: '(ohne Betreff)',
        statusSent: 'gesendet',
        statusFailed: 'fehlgeschlagen',
        statusSending: 'wird gesendet',
        statusUnknown: 'unklar',
        statusSkipped: 'übersprungen',
        inboundLabel: 'Eingehende E-Mail',
      },
      detail: {
        title: 'E-Mail-Details',
        reply: 'Antworten',
        from: 'Von',
        to: 'An',
        subject: 'Betreff',
        time: 'Zeit',
        error: 'Fehler',
        bodyNotArchived: 'Inhalt nicht archiviert — nur Metadaten',
        messageId: 'Message-ID',
        trigger: 'Auslöser',
        resend: 'Erneut senden',
        resending: 'Sende…',
        resendOk: 'E-Mail erneut gesendet',
        resendFailed: 'Erneut senden fehlgeschlagen',
        toInvoice: 'Zur Rechnung',
      },
      compose: {
        title: 'E-Mail an {{name}}',
        toLabel: 'An',
        toPlaceholder: 'patient@beispiel.com',
        languageLabel: 'Sprache',
        subjectLabel: 'Betreff',
        bodyLabel: 'Nachricht (Plaintext)',
        driftWarning: 'Abweichende Empfängeradresse (≠ Patientenakte)',
        requiredFields: 'Empfänger, Betreff und Nachricht sind erforderlich.',
        send: 'Senden',
        sending: 'Sende…',
        sent: 'E-Mail gesendet',
        sendFailed: 'Versand fehlgeschlagen',
        signatureHint: 'Grußformel und Praxis-Signatur werden automatisch angehängt.',
      },
    },
  },
  es: {
    crm: {
      state: {
        new: 'Nuevo',
        read: 'Leído',
        answered: 'Respondido',
        unmatched: 'Sin asignar',
      },
      inbox: {
        eyebrow: 'Comunicación',
        title: 'Bandeja de entrada',
        description: 'Leer, asignar y responder los correos entrantes de pacientes.',
        filterNew: 'Nuevos',
        filterUnmatched: 'Sin asignar',
        filterAnswered: 'Respondidos',
        filterAll: 'Todos',
        reload: 'Recargar',
        loading: 'Cargando…',
        errorLoad: 'No se pudo cargar la bandeja de entrada',
        emptyTitle: 'No hay mensajes',
        emptyDescription: 'No hay mensajes en este filtro.',
        loadMore: 'Cargar más',
        toPatient: 'Ir a la ficha',
        unknownSender: '(remitente desconocido)',
        smartaiLabel: 'Solicitud SmartAI (panel de la web)',
        attachments: 'Adjuntos',
        attachmentsSkipped_one: '{{count}} adjunto se omitió al recibir (tipo o tamaño no permitido).',
        attachmentsSkipped_other: '{{count}} adjuntos se omitieron al recibir (tipo o tamaño no permitido).',
        downloadFailed: 'No se pudo descargar el adjunto',
        reply: 'Responder',
        replyLoadFailed: 'No se pudieron cargar los datos del paciente',
        assign: 'Asignar paciente',
        assignTitle: 'Asignar paciente',
        assignHint: 'Este correo no se pudo asignar a ninguna ficha. Buscar y asignar paciente:',
        assignSaveAlternate: 'Guardar la dirección como correo secundario',
        assignConfirm: 'Asignar',
        assigning: 'Asignando…',
        assignOk: 'Paciente asignado',
        assignOkAlternate: 'Paciente asignado, dirección guardada como correo secundario',
        assignFailed: 'Error al asignar',
        markReadFailed: 'No se pudo marcar como leído',
      },
      timeline: {
        title: 'Comunicación',
        sendEmail: 'Enviar correo',
        showInternal: 'mostrar internos',
        hideInternal: 'ocultar internos',
        loading: 'Cargando…',
        errorLoad: 'No se pudo cargar la comunicación',
        empty: 'Aún no hay comunicación.',
        emptyFiltered: 'Solo hay entradas internas de la clínica.',
        showAll: 'Mostrar las {{count}} entradas',
        noSubject: '(sin asunto)',
        statusSent: 'enviado',
        statusFailed: 'fallido',
        statusSending: 'enviando',
        statusUnknown: 'desconocido',
        statusSkipped: 'omitido',
        inboundLabel: 'Correo entrante',
      },
      detail: {
        title: 'Detalles del correo',
        reply: 'Responder',
        from: 'De',
        to: 'Para',
        subject: 'Asunto',
        time: 'Hora',
        error: 'Error',
        bodyNotArchived: 'Contenido no archivado — solo metadatos',
        messageId: 'Message-ID',
        trigger: 'Origen',
        resend: 'Reenviar',
        resending: 'Enviando…',
        resendOk: 'Correo reenviado',
        resendFailed: 'Error al reenviar',
        toInvoice: 'Ir a la factura',
      },
      compose: {
        title: 'Correo a {{name}}',
        toLabel: 'Para',
        toPlaceholder: 'paciente@ejemplo.com',
        languageLabel: 'Idioma',
        subjectLabel: 'Asunto',
        bodyLabel: 'Mensaje (texto plano)',
        driftWarning: 'Dirección de destinatario distinta (≠ historia clínica)',
        requiredFields: 'Destinatario, asunto y mensaje son obligatorios.',
        send: 'Enviar',
        sending: 'Enviando…',
        sent: 'Correo enviado',
        sendFailed: 'Error en el envío',
        signatureHint: 'El saludo y la firma de la clínica se añaden automáticamente.',
      },
    },
  },
};
